import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { API_BASE_URL, AuthError, apiFetch } from '@/api/client'
import { queryKeys } from '@/api/queries'
import { POLL_NORMAL } from '@/api/polling'
import { invoiceKeys } from '@/features/invoices/keys'
import { useAuth } from '@/hooks/use-auth'
import { scopeQuery } from './model'
import type {
  ChaseChoice,
  ChaseOwner,
  OwedItem,
  OwedItemCreate,
  OwedKind,
  OwedOverviewItem,
  OwedScope,
} from './types'

export const owedKeys = {
  all: ['owed-items'] as const,
  list: (scope: OwedScope) => ['owed-items', 'list', scopeQuery(scope)] as const,
  kinds: ['owed-items', 'kinds'] as const,
  overview: (role: string) => ['owed-items', 'overview', role] as const,
}

const BASE = '/dashboard/owed-items'

/**
 * Calls the owed-items routes. Errors are thrown like openapi-fetch throws them (the parsed
 * FastAPI body), so ``formatApiErrorMessage`` reads their ``detail``.
 */
function useOwedRequest() {
  const { getAccessToken } = useAuth()
  return async function request<T>(path: string, init?: { method?: string; json?: unknown }): Promise<T> {
    const token = await getAccessToken()
    if (!token) throw new AuthError(401, 'Failed to acquire access token')
    const response = await apiFetch(`${API_BASE_URL}${BASE}${path}`, {
      method: init?.method ?? 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: init?.json === undefined ? undefined : JSON.stringify(init.json),
    })
    const body: unknown = await response.json().catch(() => null)
    if (!response.ok) throw body ?? new Error(`Request failed with ${response.status}`)
    return body as T
  }
}

/** A record's owed items: waiting first by due date, then the most recent closed ones. */
export function useOwedItems(scope: OwedScope) {
  const request = useOwedRequest()
  return useQuery({
    queryKey: owedKeys.list(scope),
    queryFn: () => {
      const params = new URLSearchParams({ ...scopeQuery(scope), include_closed: 'true' })
      return request<OwedItem[]>(`?${params.toString()}`)
    },
    refetchInterval: POLL_NORMAL,
  })
}

/** Everything still owed across all orders, for one team or all (USA-1444). */
export function useOwedOverview(role: string) {
  const request = useOwedRequest()
  return useQuery({
    queryKey: owedKeys.overview(role),
    queryFn: () => request<OwedOverviewItem[]>(role === 'all' ? '/overview' : `/overview?role=${encodeURIComponent(role)}`),
    refetchInterval: POLL_NORMAL,
  })
}

/** The kinds staff can add by hand. */
export function useOwedKinds(enabled: boolean) {
  const request = useOwedRequest()
  return useQuery({
    queryKey: owedKeys.kinds,
    queryFn: () => request<OwedKind[]>('/kinds'),
    enabled,
    staleTime: Infinity,
  })
}

export function useCreateOwedItem(scope: OwedScope) {
  const request = useOwedRequest()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: OwedItemCreate) => request<OwedItem>('', { method: 'POST', json: body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: owedKeys.list(scope) }),
  })
}

/** Received or not needed: the row moves to the closed list at once and comes back if the save fails. */
export function useSettleOwedItem(scope: OwedScope) {
  const request = useOwedRequest()
  const queryClient = useQueryClient()
  const key = owedKeys.list(scope)
  return useMutation({
    mutationFn: ({
      uuid,
      status,
      note,
      outcome,
    }: {
      uuid: string
      status: 'received' | 'waived'
      note?: string
      outcome?: 'refund' | 'credit'
    }) => request<OwedItem>(`/${encodeURIComponent(uuid)}/settle`, { method: 'POST', json: { status, note, outcome } }),
    onMutate: async ({ uuid, status }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<OwedItem[]>(key)
      const settledAt = new Date().toISOString()
      queryClient.setQueryData<OwedItem[]>(key, (old) =>
        old?.map((item) =>
          item.uuid === uuid ? { ...item, status, settled_at: settledAt, days_late: 0 } : item,
        ),
      )
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
      // Settling the last item on a chase closes its action item.
      queryClient.invalidateQueries({ queryKey: scopeOwnerKey(scope) })
      invalidateActionHolders(queryClient)
    },
  })
}

export function useRescheduleOwedItem(scope: OwedScope) {
  const request = useOwedRequest()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, dueOn, note }: { uuid: string; dueOn: string; note?: string }) =>
      request<OwedItem>(`/${encodeURIComponent(uuid)}/reschedule`, {
        method: 'POST',
        json: { due_on: dueOn, note },
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: owedKeys.list(scope) }),
  })
}

function scopeOwnerKey(scope: OwedScope): QueryKey {
  if (scope.kind === 'purchase_order') return queryKeys.purchaseOrders.detail(scope.poUuid)
  if (scope.kind === 'ap_invoice') return invoiceKeys.detail(scope.invoiceUuid)
  return queryKeys.customerComms.detail(scope.threadId)
}

function ownerKey(owner: ChaseOwner): QueryKey {
  if (owner.source === 'purchase_order') return queryKeys.purchaseOrders.detail(owner.poUuid)
  if (owner.source === 'ap_invoice') return invoiceKeys.detail(owner.invoiceUuid)
  return queryKeys.customerComms.detail(owner.threadId)
}

/** Action items count on the Action Items page and the home summary. */
function invalidateActionHolders(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
  queryClient.invalidateQueries({ queryKey: queryKeys.dashboardSummary.all })
}

/** Close an "owed_overdue" chase card: chased with a new due date, called, received, or no longer needed. */
export function useResolveChase(owner: ChaseOwner) {
  const request = useOwedRequest()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ actionId, choice, dueOn, note }: { actionId: number; choice: ChaseChoice; dueOn?: string; note?: string }) =>
      request<unknown>(`/chase-actions/${owner.source}/${actionId}/resolve`, {
        method: 'POST',
        json: { choice, due_on: dueOn, note },
      }),
    onSettled: (_data, _error, { choice }) => {
      queryClient.invalidateQueries({ queryKey: owedKeys.all })
      queryClient.invalidateQueries({ queryKey: ownerKey(owner) })
      invalidateActionHolders(queryClient)
      // A recorded call is a new entry in the PO's conversation.
      if (choice === 'called') queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
    },
  })
}
